import React, { useState, useEffect, useRef, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { onSnapshot, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import * as LucideIcons from 'lucide-react';
import { motion } from 'framer-motion';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import CosmicCountdown from './CosmicCountdown';
import LotteryTicket from './LotteryTicket';
import GlobalParticipationTicker from './GlobalParticipationTicker';
import UpcomingDrawCard from './UpcomingDrawCard';
import VisualLotteryGuide from './VisualLotteryGuide';
import DrawAnimation from './DrawAnimation';
import Celebration from './Celebration';
import LotterySyndicate from './LotterySyndicate';
import GiftTicketModal from './GiftTicketModal';
import BadgeIconStrip from './BadgeIconStrip';

// --- HELPER & SKELETON COMPONENTS ---

import { useTranslation } from 'react-i18next';

// --- HELPER & SKELETON COMPONENTS ---

const ConsolationPrizes = ({ winners, onComplete }) => {
    const { t } = useTranslation();
    useEffect(() => {
        const timer = setTimeout(onComplete, 5000);
        return () => clearTimeout(timer);
    }, [onComplete]);

    if (!winners || winners.length === 0) return null;

    return (
        <div className="fixed bottom-4 right-4 z-50">
            <motion.div 
                className="bg-gray-800 border border-purple-500 rounded-lg shadow-lg p-4 w-80"
                initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
            >
                <h4 className="text-lg font-bold text-purple-300 mb-2">{t('lotteryPage.consolationPrizes')}</h4>
                <ul className="space-y-2">
                    {winners.map((winner, index) => (
                        <motion.li 
                            key={winner.userId}
                            className="flex justify-between items-center text-sm"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0, transition: { delay: 0.5 + index * 0.3 } }}
                        >
                            <span className="text-gray-300">{winner.displayName}</span>
                            <span className="font-bold text-green-400">+{t('lotteryPage.echoes', { count: winner.prizeAmount })}</span>
                        </motion.li>
                    ))}
                </ul>
            </motion.div>
        </div>
    );
};

const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => { ref.current = value; });
    return ref.current;
};

const AnimatedNumber = ({ value }) => {
    const { t } = useTranslation();
    const [currentValue, setCurrentValue] = useState(value);
    const prevValue = usePrevious(value);
    useEffect(() => {
        if (prevValue === undefined || prevValue === value) { setCurrentValue(value); return; }
        let start = prevValue;
        const end = value;
        const duration = 1000;
        const range = end - start;
        let startTime = null;
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCurrentValue(Math.floor(start + range * progress));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [value, prevValue]);
    return <span>{t('lotteryPage.number', { value: currentValue })}</span>;
};

const ParticleBurst = ({ x, y, active }) => {
    if (!active) return null;
    const particles = Array.from({ length: 15 });
    return (
        <div className="absolute" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
            {particles.map((_, i) => {
                const angle = (i / particles.length) * 360;
                const distance = Math.random() * 50 + 50;
                const size = Math.random() * 8 + 4;
                return <div key={i} className="absolute bg-yellow-400 rounded-full animate-burst" style={{ '--angle': `${angle}deg`, '--distance': `${distance}px`, '--size': `${size}px`, width: size, height: size }} />;
            })}
        </div>
    );
};

const OddsGauge = ({ odds }) => {
    const { t } = useTranslation();
    const clampedOdds = Math.max(0, Math.min(100, odds));
    const angle = (clampedOdds / 100) * 180;
    const RADIUS = 50, STROKE_WIDTH = 12, CX = 60, CY = 60;
    const arcPath = `M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`;
    return (
        <div className="relative flex justify-center items-center h-24">
            <svg width="120" height="70" viewBox="0 0 120 70" className="overflow-visible">
                <defs><linearGradient id="oddsGradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#48BB78" /><stop offset="50%" stopColor="#F6E05E" /><stop offset="100%" stopColor="#F56565" /></linearGradient></defs>
                <path d={arcPath} stroke="#4A5568" strokeWidth={STROKE_WIDTH} strokeLinecap="round" fill="none" />
                <path d={arcPath} stroke="url(#oddsGradient)" strokeWidth={STROKE_WIDTH} strokeLinecap="round" fill="none" />
                <g transform={`rotate(${angle} ${CX} ${CY})`}><line x1={CX} y1={CY} x2={CX - RADIUS - 5} y2={CY} stroke="#E2E8F0" strokeWidth="2" style={{ transition: 'transform 0.5s ease-out' }} /><circle cx={CX} cy={CY} r="4" fill="#E2E8F0" /></g>
            </svg>
            <div className="absolute bottom-2 text-center"><span className="text-2xl font-bold text-white celestial-numbers">{t('lotteryPage.odds', { odds: odds.toFixed(2) })}</span></div>
        </div>
    );
};

const LotteryPageSkeleton = () => (
    <div className="p-6 bg-gray-800/30 rounded-2xl animate-pulse">
        <div className="h-8 bg-gray-700/50 rounded-md w-1/3 mx-auto mb-6"></div>
        <div className="h-24 bg-gray-700/50 rounded-lg w-1/2 mx-auto mb-4"></div>
        <div className="h-8 bg-gray-700/50 rounded-md w-full mb-4"></div>
        <div className="flex justify-end mb-4"><div className="h-8 w-24 bg-gray-700/50 rounded-md"></div></div>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="h-48 bg-gray-700/50 rounded-xl"></div>
            <div className="h-48 bg-gray-700/50 rounded-xl"></div>
        </div>
        <div className="h-24 bg-gray-700/50 rounded-xl mb-8"></div>
        <div className="grid md:grid-cols-2 gap-8">
            <div className="h-60 bg-gray-700/50 rounded-xl"></div>
            <div className="h-60 bg-gray-700/50 rounded-xl"></div>
        </div>
    </div>
);

// --- MAIN LOTTERY PAGE COMPONENT ---

const LotteryPage = () => {
    const { t } = useTranslation();
    const { db, user, showToast, appId, userId, setMessage, showConfirmation, currentUserProfile, handlePageChange, appFunctions } = useAppContext();
    const [lotteryStatus, setLotteryStatus] = useState(null);
    const [myEntries, setMyEntries] = useState([]);
    const [pastWinners, setPastWinners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setLocalError] = useState(null);
    const [amount, setAmount] = useState(10);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDrawAnimation, setShowDrawAnimation] = useState(false);
    const [currentWinner, setCurrentWinner] = useState(null);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [justPurchasedTicketId, setJustPurchasedTicketId] = useState(null);
    const [burst, setBurst] = useState({ active: false, x: 0, y: 0 });
    const [isPotPulsing, setIsPotPulsing] = useState(false);
    const [isLocking, setIsLocking] = useState(false);
    const [drawWinnerInfo, setDrawWinnerInfo] = useState(null);
    const [consolationWinners, setConsolationWinners] = useState([]);
    const [myCharms, setMyCharms] = useState({ ECHOES_MULTIPLIER: 0 });
    const [activeCharm, setActiveCharm] = useState(null);
    const [entryType, setEntryType] = useState('personal');
    const [activeTab, setActiveTab] = useState('draw');
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [personalHistory, setPersonalHistory] = useState([]);
    const [historyStats, setHistoryStats] = useState({ totalSpent: 0, totalWon: 0 });
    const [leaderboard, setLeaderboard] = useState([]);

    const userTotalEntries = useMemo(() => myEntries.reduce((sum, entry) => sum + (entry.amount || 0) + (entry.bonusTickets || 0), 0), [myEntries]);
    const totalLotteryTickets = lotteryStatus?.totalTickets || 0;
    const userOdds = useMemo(() => totalLotteryTickets > 0 ? (userTotalEntries / totalLotteryTickets) * 100 : 0, [userTotalEntries, totalLotteryTickets]);
    const maxPrize = useMemo(() => pastWinners.length === 0 ? 0 : Math.max(...pastWinners.map(w => w.prizeAmount)), [pastWinners]);

    const prevTotalPot = usePrevious(lotteryStatus?.totalPot);

    useEffect(() => {
        if (!appId) return;
        const q = query(collection(db, `artifacts/${appId}/public/data/lottery/current/entries`), orderBy('amount', 'desc'), limit(5));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setLeaderboard(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [appId, db]);

    useEffect(() => {
        if (!userId || pastWinners.length === 0) return;
        let totalSpent = 0, totalWon = 0;
        const processedHistory = pastWinners.map(draw => {
            const spentInDraw = draw.participants?.[userId] || 0;
            if (spentInDraw > 0) {
                totalSpent += spentInDraw;
                let wonInDraw = 0;
                if (draw.winnerId === userId) {
                    wonInDraw = draw.prizeAmount;
                    totalWon += wonInDraw;
                }
                return { id: draw.id, drawDate: draw.drawDate.toDate(), spent: spentInDraw, won: wonInDraw, wasWinner: draw.winnerId === userId };
            }
            return null;
        }).filter(Boolean);
        setPersonalHistory(processedHistory);
        setHistoryStats({ totalSpent, totalWon });
    }, [pastWinners, userId]);

    useEffect(() => {
        if (!userId || !db) return;
        const charmsRef = collection(db, `artifacts/${appId}/users/${userId}/charms`);
        const unsubscribe = onSnapshot(charmsRef, (snapshot) => {
            const charmsData = { ECHOES_MULTIPLIER: 0 };
            snapshot.forEach(doc => {
                charmsData[doc.id] = doc.data().quantity;
            });
            setMyCharms(charmsData);
        });
        return () => unsubscribe();
    }, [userId, db, appId]);

    const handleBuyCharm = async (charmType) => {
        setIsSubmitting(true);
        const buyCharm = httpsCallable(appFunctions, 'buyLotteryCharm');
        try {
            await buyCharm({ charmType });
            setMessage(t('lotteryPage.charmSuccess'), 'success');
        } catch (err) { 
            console.error("Error buying charm:", err); 
            setLocalError(err.message); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    useEffect(() => {
        if (prevTotalPot !== undefined && lotteryStatus?.totalPot > prevTotalPot) {
            setIsPotPulsing(true);
            setTimeout(() => setIsPotPulsing(false), 1000);
        }
    }, [lotteryStatus?.totalPot, prevTotalPot]);

    const handleCountdownComplete = async () => {
        setIsLocking(true);
        try {
            const drawLotteryWinner = httpsCallable(appFunctions, 'drawLotteryWinner');
            const result = await drawLotteryWinner();
            const { winner, consolationWinners } = result.data;
            setDrawWinnerInfo(winner);
            setCurrentWinner(winner);
            setConsolationWinners(consolationWinners || []);
            if (winner && winner.winnerId === userId) setMessage(t('lotteryPage.victoryEcho', { amount: winner.prizeAmount.toLocaleString() }));
            setTimeout(() => { setIsLocking(false); setShowDrawAnimation(true); }, 2000);
            const getLotteryStatus = httpsCallable(appFunctions, 'getLotteryStatus');
            const statusResult = await getLotteryStatus();
            setLotteryStatus(statusResult.data);
        } catch (err) { console.error("Error drawing winner:", err); setLocalError(t('lotteryPage.drawError')); setIsLocking(false); }
    };

    useEffect(() => {
        const getLotteryData = async () => {
            setIsLoading(true);
            try {
                const getLotteryStatus = httpsCallable(appFunctions, 'getLotteryStatus');
                const statusResult = await getLotteryStatus();
                setLotteryStatus(statusResult.data);
                if (statusResult.data && statusResult.data.drawDate && new Date(statusResult.data.drawDate) < new Date() && !statusResult.data.winnerId) {
                    setShowDrawAnimation(true);
                } else { setShowDrawAnimation(false); }
                const entriesQuery = query(collection(db, `artifacts/${appId}/public/data/lottery/current/entries`), where('userId', '==', userId));
                const entriesSnapshot = await getDocs(entriesQuery);
                setMyEntries(entriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                const winnersQuery = query(collection(db, `artifacts/${appId}/public/data/lottery_history`), orderBy('drawDate', 'desc'), limit(10));
                const winnersSnapshot = await getDocs(winnersQuery);
                setPastWinners(winnersSnapshot.docs.map(doc => doc.data()));
            } catch (err) { console.error("Error fetching lottery data:", err); setLocalError(t('lotteryPage.loadError')); } finally { setIsLoading(false); }
        };
        getLotteryData();
    }, [appFunctions, userId, db, appId, t]);

    const handleEnterLottery = async (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left, y = e.clientY - rect.top;
        setBurst({ active: true, x, y });
        setTimeout(() => setBurst({ active: false, x: 0, y: 0 }), 700);
        setIsSubmitting(true);
        setLocalError(null);
        try {
            const enterLottery = httpsCallable(appFunctions, 'enterLottery');
            const payload = { amount, useCharm: entryType === 'personal' ? activeCharm : null, syndicateId: entryType === 'syndicate' ? currentUserProfile?.activeSyndicateId : null };
            await enterLottery(payload);
            setAmount(10);
            setActiveCharm(null);
            const getLotteryStatus = httpsCallable(appFunctions, 'getLotteryStatus');
            const statusResult = await getLotteryStatus();
            setLotteryStatus(statusResult.data);
            const entriesQuery = query(collection(db, `artifacts/${appId}/public/data/lottery/current/entries`), where('userId', '==', userId));
            const entriesSnapshot = await getDocs(entriesQuery);
            const newEntries = entriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMyEntries(newEntries);
            if (newEntries.length > 0) {
                setJustPurchasedTicketId(newEntries[newEntries.length - 1].id);
                setTimeout(() => setJustPurchasedTicketId(null), 1000);
            }
        } catch (err) { console.error("Error entering lottery:", err); setLocalError(err.message); } finally { setIsSubmitting(false); }
    };

    const handleQuickPick = () => setAmount(Math.floor(Math.random() * 90) + 10);
    const handleShareVictory = (winner) => setMessage(`I just won ${winner.prizeAmount.toLocaleString()} Echoes in the Echo's Fortune lottery! #WhispersOfHarmony #Jackpot`);

    if (isLoading) return <LotteryPageSkeleton />;
    if (showDrawAnimation) return <DrawAnimation winner={drawWinnerInfo} onAnimationComplete={() => setShowDrawAnimation(false)} />;

    return (
        <div className={`p-6 bg-gray-800/30 rounded-2xl cosmic-dust-bg ${lotteryStatus?.totalPot > 1000000 ? 'fever-mode' : ''}`}>
            <ParticleBurst {...burst} />
            {currentWinner && <Celebration winner={currentWinner} onComplete={() => setCurrentWinner(null)} onShare={() => handleShareVictory(currentWinner)} />}
            <ConsolationPrizes winners={consolationWinners} onComplete={() => setConsolationWinners([])} />
            <h2 className={`text-3xl font-bold text-center mb-6 font-playfair ${lotteryStatus?.totalPot > 1000000 ? 'text-yellow-300 animate-pulse' : 'text-purple-300'}`}>{lotteryStatus?.totalPot > 1000000 ? t('lotteryPage.jackpotFever') : t('lotteryPage.title')}</h2>
            {lotteryStatus?.drawDate && <CosmicCountdown drawDate={lotteryStatus.drawDate} onComplete={handleCountdownComplete} />}
            <GlobalParticipationTicker />
            <div className="flex justify-center border-b border-gray-700 mb-4">
                <button onClick={() => setActiveTab('draw')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'draw' ? 'text-purple-300 border-b-2 border-purple-300' : 'text-gray-400'}`}>{t('lotteryPage.tabs.draw')}</button>
                <button onClick={() => setActiveTab('syndicates')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'syndicates' ? 'text-cyan-300 border-b-2 border-cyan-300' : 'text-gray-400'}`}>{t('lotteryPage.tabs.syndicates')}</button>
                <button onClick={() => setActiveTab('history')} className={`px-4 py-2 text-sm font-medium ${activeTab === 'history' ? 'text-yellow-300 border-b-2 border-yellow-300' : 'text-gray-400'}`}>{t('lotteryPage.tabs.history')}</button>
            </div>
            <div className="flex justify-end mb-4 gap-2">
                <button onClick={() => setShowGiftModal(true)} className="small-action-button bg-cyan-600 hover:bg-cyan-700 text-white"><LucideIcons.Gift size={16} className="mr-2" />{t('lotteryPage.giftTicket')}</button>
                <button onClick={() => setShowRulesModal(true)} className="small-action-button bg-gray-700 hover:bg-gray-600 text-white"><LucideIcons.BookOpen size={16} className="mr-2" />{t('lotteryPage.howToPlay')}</button>
            </div>
            {error && <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg mb-4">{error}</div>}
            {showGiftModal && <GiftTicketModal onClose={() => setShowGiftModal(false)} />}
            {activeTab === 'draw' && (
                <>
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div className={`relative bg-gray-900/50 p-6 rounded-xl border border-purple-500/30 overflow-hidden ${isLocking ? 'animate-metallic-sheen' : ''}`}><h3 className="font-bold text-xl mb-4 text-yellow-300">{t('lotteryPage.currentPot')} <span className="text-purple-400 text-sm ml-2">({t('lotteryPage.jackpot')})</span></h3><div className="text-5xl font-bold text-yellow-200 flex items-center justify-center animate-pulse-grow celestial-numbers animate-shimmer"><LucideIcons.Gem size={48} className="mr-3 text-yellow-400" /><AnimatedNumber value={lotteryStatus?.totalPot || 0} /></div><div className="text-gray-400 text-center">{t('lotteryPage.participants', { count: lotteryStatus?.participantCount || 0 })}</div><p className="text-xs text-gray-500 mt-4 text-center">{t('lotteryPage.nextDrawing', { date: new Date(lotteryStatus?.drawDate).toLocaleString() })}</p><div className={`absolute bottom-0 left-0 w-full bg-purple-500/20 transition-all duration-500 ease-out ${isPotPulsing ? 'animate-pot-pulse' : ''}`} style={{ height: `${Math.min(100, (lotteryStatus?.totalPot || 0) / 1000 * 10)}%` }}></div></div>
                        <div className={`bg-gray-900/50 p-6 rounded-xl border border-green-500/30 transition-all duration-500 ${isLocking ? 'opacity-50 grayscale pointer-events-none' : ''}`}><h3 className="font-bold text-xl mb-4 text-green-300">{t('lotteryPage.enterNow')}</h3>{currentUserProfile?.activeSyndicateId && (<div className="mb-4"><label className="text-sm text-gray-400">{t('lotteryPage.contributeTo')}</label><div className="flex items-center bg-gray-800 rounded-lg p-1 mt-1"><button onClick={() => setEntryType('personal')} className={`flex-1 text-center text-sm rounded-md py-1 ${entryType === 'personal' ? 'bg-purple-600 text-white' : 'text-gray-300'}`}>{t('lotteryPage.personal')}</button><button onClick={() => setEntryType('syndicate')} className={`flex-1 text-center text-sm rounded-md py-1 ${entryType === 'syndicate' ? 'bg-cyan-600 text-white' : 'text-gray-300'}`}>{t('lotteryPage.syndicate')}</button></div></div>)}<form onSubmit={(e) => handleEnterLottery(e)}><div className="flex items-center mb-4"><input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} min="1" className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white mr-2" disabled={isLocking || isSubmitting} /><button type="button" onClick={handleQuickPick} className="small-action-button bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0 transition-transform duration-200 hover:scale-105 active:scale-95" disabled={isLocking || isSubmitting}><LucideIcons.Dices size={16} className="mr-1" /> {t('lotteryPage.quickPick')}</button></div><button type="submit" disabled={isLocking || isSubmitting} className="relative overflow-hidden w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed">{isSubmitting ? t('lotteryPage.entering') : t('lotteryPage.enterWith', { amount })}</button></form></div>
                    </div>
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-yellow-500/30 mb-8"><h3 className="font-bold text-xl mb-4 text-yellow-300">{t('lotteryPage.luckyCharms')}</h3><div className="grid md:grid-cols-2 gap-4 items-center"><div><p className="font-semibold text-lg text-white">{t('lotteryPage.echoesMultiplier')}</p><p className="text-sm text-gray-400">{t('lotteryPage.echoesMultiplierDescription')}</p><p className="text-xs text-yellow-500 mt-1">{t('lotteryPage.cost', { amount: 250 })}</p></div><div className="flex items-center gap-2"><div className="text-center"><p className="text-xs text-gray-400">{t('lotteryPage.owned')}</p><p className="text-2xl font-bold text-white">{myCharms.ECHOES_MULTIPLIER || 0}</p></div><button onClick={() => handleBuyCharm('ECHOES_MULTIPLIER')} disabled={isSubmitting} className="small-action-button bg-blue-600 hover:bg-blue-700 text-white"><LucideIcons.Plus size={16} /> {t('lotteryPage.buy')}</button><button type="button" onClick={() => setActiveCharm(activeCharm === 'ECHOES_MULTIPLIER' ? null : 'ECHOES_MULTIPLIER')} disabled={isSubmitting || (myCharms.ECHOES_MULTIPLIER || 0) === 0 || entryType === 'syndicate'} className={`small-action-button transition-all duration-200 ${activeCharm === 'ECHOES_MULTIPLIER' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-500'} ${entryType === 'syndicate' ? 'opacity-50 cursor-not-allowed' : ''}`}>{activeCharm === 'ECHOES_MULTIPLIER' ? <><LucideIcons.CheckCircle size={16} /> {t('lotteryPage.activated')}</> : <><LucideIcons.Zap size={16} /> {t('lotteryPage.activate')}</>}</button></div></div></div>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className={`relative overflow-hidden ${isLocking ? 'animate-metallic-sheen' : ''}`}>
                            <h3 className="font-bold text-xl mb-4 text-blue-300">{t('lotteryPage.myTickets')}</h3>
                            {userTotalEntries > 0 && (<div className="bg-gray-900/50 p-4 rounded-xl border border-blue-500/30 mb-4 text-center"><h4 className="text-lg font-bold text-white mb-2">{t('lotteryPage.yourOdds')}</h4><OddsGauge odds={userOdds} /><p className="text-sm text-gray-400 mt-2">({t('lotteryPage.ticketStats', { count: userTotalEntries, total: totalLotteryTickets })})</p></div>)}
                            <div className="bg-gray-900/50 p-4 rounded-xl max-h-60 overflow-y-auto border border-blue-500/30 space-y-3">{myEntries.length > 0 ? (myEntries.map((entry) => (<LotteryTicket key={entry.id} entry={entry} isMyTicket={true} justPurchasedTicketId={justPurchasedTicketId} totalPot={lotteryStatus?.totalPot || 0} photoURL={currentUserProfile?.photoURL} />))) : (<div className="text-center text-gray-500 p-8"><LucideIcons.Ticket size={32} className="mx-auto mb-2" /><h5 className="font-bold">{t('lotteryPage.noTickets')}</h5><p className="text-sm">{t('lotteryPage.noTicketsDescription')}</p></div>)}</div>
                        </div>
                        <div>
                            <h3 className="font-bold text-xl mb-4 text-pink-300">{t('lotteryPage.hallOfFame')}</h3>
                            <div className="bg-gray-900/50 p-4 rounded-xl max-h-96 overflow-y-auto border border-pink-500/30 space-y-3">
                                {pastWinners.length > 0 ? (pastWinners.map((winner, index) => {
                                    const isBiggestWinner = winner.prizeAmount === maxPrize;
                                    return (
                                        <div key={index} className={`p-3 rounded-lg bg-gray-800/70 shadow-md border-l-4 ${isBiggestWinner ? 'border-yellow-400' : 'border-pink-500'}`}>
                                            <div className="flex items-center gap-3">
                                                {isBiggestWinner && <LucideIcons.Crown size={24} className="text-yellow-400 flex-shrink-0" />}
                                                {!isBiggestWinner && <LucideIcons.Trophy size={24} className="text-pink-400 flex-shrink-0" />}
                                                <div className="flex-grow">
                                                    <p className="font-semibold text-white">{winner.winnerDisplayName || winner.winnerId.substring(0, 8) + '...'}<BadgeIconStrip badges={winner.winnerBadges} /></p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })) : (
                                    <div className="text-center text-gray-500 p-8">
                                        <LucideIcons.Trophy size={32} className="mx-auto mb-2" />
                                        <h5 className="font-bold">{t('lotteryPage.noWinners')}</h5>
                                        <p className="text-sm">{t('lotteryPage.noWinnersDescription')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
            {activeTab === 'syndicates' && <LotterySyndicate />}
            {activeTab === 'history' && (
                <div className="animate-fade-in">
                    <h3 className="font-bold text-xl mb-4 text-yellow-300">{t('lotteryPage.myHistory')}</h3>
                    <div className="grid grid-cols-2 gap-4 mb-6 text-center"><div className="bg-gray-900/50 p-4 rounded-lg"><p className="text-sm text-gray-400">{t('lotteryPage.totalSpent')}</p><p className="text-2xl font-bold text-red-400">{t('lotteryPage.echoes', { count: historyStats.totalSpent })}</p></div><div className="bg-gray-900/50 p-4 rounded-lg"><p className="text-sm text-gray-400">{t('lotteryPage.totalWon')}</p><p className="text-2xl font-bold text-green-400">{t('lotteryPage.echoes', { count: historyStats.totalWon })}</p></div></div>
                    <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">{personalHistory.length > 0 ? (personalHistory.map(item => (<div key={item.id} className={`p-3 rounded-lg flex justify-between items-center ${item.wasWinner ? 'bg-yellow-900/30 border-l-4 border-yellow-500' : 'bg-gray-800/70'}`}><div><p className="font-semibold text-white">{t('lotteryPage.drawFrom', { date: item.drawDate.toLocaleString() })}</p><p className="text-xs text-gray-400">{t('lotteryPage.youSpent', { amount: item.spent })}</p></div><div>{item.wasWinner ? (<p className="font-bold text-green-400">{t('lotteryPage.youWon', { amount: item.won.toLocaleString() })}</p>) : (<p className="text-sm text-gray-500">{t('lotteryPage.betterLuck')}</p>)}</div></div>))) : (<p className="text-center text-gray-500 py-8">{t('lotteryPage.noHistory')}</p>)}</div>
                </div>
            )}
            {activeTab === 'draw' && (
                <div className="mt-8">
                    <h3 className="font-bold text-xl mb-4 text-teal-300">{t('lotteryPage.topEntrants')}</h3>
                    <div className="space-y-2">{leaderboard.map((entry, index) => (<div key={entry.id} className="flex items-center bg-gray-800/70 p-2 rounded-lg text-white"><span className="w-6 text-center font-bold text-teal-400">{index + 1}</span><img src={entry.photoURL || 'https://placehold.co/100x100/AEC6CF/FFFFFF?text=U'} alt={entry.displayName} className="w-8 h-8 rounded-full mx-3"/><span className="flex-grow font-semibold">{entry.displayName}<BadgeIconStrip badges={entry.badges} /></span><span className="font-bold text-green-400">{entry.amount.toLocaleString()}</span></div>))}</div>
                </div>
            )}
            <UpcomingDrawCard nextDrawDate={lotteryStatus?.drawDate} />
            {showRulesModal && <VisualLotteryGuide onClose={() => setShowRulesModal(false)} />}
        </div>
    );
};

export default LotteryPage;